import { creatorbase } from '@round/api';
import { ComboboxItem } from '@round/creatorbase-ui';
import { useUsers, useUsersListInfinite } from '@round/creatorbase-helpers/api-hooks';
import { debounce } from 'lodash';
import { UIEventHandler, useCallback, useMemo, useState } from 'react';
import { useCurrentCreatorbaseUser } from 'Modules/Auth/hooks/useCurrentCreatorbaseUser';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 500;
const LIST_SCROLL_THRESHOLD_PX = 80;

const ME_LABEL = 'Me';

export type UserItem = ComboboxItem<number>;

const mapUserToItem = (user: creatorbase.User): UserItem => ({ value: user.id, label: user.name });

type Params = {
    selectedId: string | undefined;
    clientId: number | undefined;
};

export function useCreatedByCombobox({ selectedId, clientId }: Params) {
    const { creatorbaseUserId } = useCurrentCreatorbaseUser();
    const [isOpen, setIsOpen] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const setSearch = useMemo(() => debounce(setDebouncedSearch, SEARCH_DEBOUNCE_MS), []);

    const isMeSelected = Boolean(selectedId) && selectedId === String(creatorbaseUserId);

    // Resolve the selected user by id so the input stays labelled even after a search narrows the
    // list away from them. The filter state only holds the id.
    const { data: selectedUserData } = useUsers(
        { id: selectedId, page_size: 1 },
        { enabled: Boolean(selectedId) && !isMeSelected }
    );

    const { data, isError, isFetching, hasNextPage, fetchNextPage } = useUsersListInfinite(
        { client_id: clientId, page_size: PAGE_SIZE, search: debouncedSearch },
        // Defer the list until the menu opens; the selected value is resolved separately.
        { enabled: typeof clientId === 'number' && isOpen }
    );

    const items = useMemo(() => {
        const users = data?.pages.flatMap((page) => page.results) ?? [];
        if (typeof creatorbaseUserId !== 'number') {
            return users.map(mapUserToItem);
        }

        // The signed-in user leads the list as "Me", so their own name is never offered twice.
        const others = users.filter((user) => user.id !== creatorbaseUserId).map(mapUserToItem);
        return [{ value: creatorbaseUserId, label: ME_LABEL }, ...others];
    }, [data, creatorbaseUserId]);

    const selectedItem = useMemo((): UserItem | null => {
        if (isMeSelected && typeof creatorbaseUserId === 'number') {
            return { value: creatorbaseUserId, label: ME_LABEL };
        }

        const [user] = selectedUserData?.results ?? [];
        if (!user) {
            return null;
        }

        return mapUserToItem(user);
    }, [isMeSelected, creatorbaseUserId, selectedUserData]);

    const onInputValueChange = useCallback((value: string) => setSearch(value), [setSearch]);

    const onItemsListScroll = useCallback<UIEventHandler<HTMLDivElement>>(
        (event) => {
            const el = event.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight > LIST_SCROLL_THRESHOLD_PX) {
                return;
            }
            if (hasNextPage && !isFetching) {
                void fetchNextPage();
            }
        },
        [fetchNextPage, hasNextPage, isFetching]
    );

    return {
        items,
        selectedItem,
        open: isOpen,
        onOpenChange: setIsOpen,
        onInputValueChange,
        onItemsListScroll,
        /** Disables base-ui client filtering; the list is driven by the debounced API search. */
        filter: null,
        noItemsFound: isError ? 'Could not get users' : 'No users found',
    };
}
